import { COMMA, ENTER } from "@angular/cdk/keycodes";
import { ChangeDetectorRef, Component, ElementRef, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from "@angular/core";
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatExpansionPanel } from "@angular/material/expansion";
import { MatSlideToggleChange } from "@angular/material/slide-toggle";
import { Router } from "@angular/router";
import { FileLogger, Tools } from "@comunicare/comunicare-helper";
import {
  ACTIVITY_CATEGORY_NAME,
  Careplan,
  FHIRHelper,
  ICareplan,
  IReference,
  ITiming,
  ITranslation,
} from "@comunicare/comunicare-interfaces";
import { TimingEditorComponent } from "@comunicare/comunicare-timing-editor";
import { Subject, Subscription } from "rxjs";
import { skipWhile, takeUntil, tap } from "rxjs/operators";
import { CareplanEditorService } from "src/app/careplan-editor/domain/careplan-editor.service";
import { CareplanTemplateFormHelper } from "src/app/careplan-editor/domain/careplan-template-form-helper";
import { Healthcareservice } from "src/app/models/healthcareservice.model";
import { CareplansService } from "src/app/providers/careplans.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { LanguagesService } from "src/app/providers/languages.service";
import { SessionService } from "src/app/providers/session.service";
import uuid from "uuid-random";
import { VIEWS } from "../careplan-editor-general-tab.component";

@Component({
  selector: "app-careplan-visual-view",
  templateUrl: "./careplan-visual-view.component.html",
  styleUrls: ["./careplan-visual-view.component.scss"],
})
export class CareplanVisualViewComponent implements OnInit, OnDestroy {
  @ViewChild("input") importInput: ElementRef;
  @ViewChild("productListDrugInput") productListDrugInput: ElementRef<HTMLInputElement>;
  @ViewChild(TimingEditorComponent) timing: TimingEditorComponent;
  public panelCount;
  public previousPanels: MatExpansionPanel[];
  public availablePeriodUnits: { value: string; translationKey: string }[] = [
    { value: "h", translationKey: "periodUnit.hour" },
    { value: "d", translationKey: "periodUnit.day" },
    { value: "w", translationKey: "periodUnit.week" },
  ];

  @ViewChildren(MatExpansionPanel)
  set _expansionPanels(panels: QueryList<MatExpansionPanel>) {
    setTimeout(() => {
      const lastPanel = panels.find((obj1) => !this.previousPanels?.some((obj2) => obj1.id === obj2.id));
      lastPanel?.open();

      this.previousPanels = panels.toArray();
      this.panelCount = panels.length;
    });
  }

  public isLoading = true;
  public auto: ElementRef; // the ref of the app-drug-server-side-search's matAutoComplete input
  public careplanTemplate: ICareplan;
  public activeView = 1;
  public views = VIEWS;
  public currentView: VIEWS = this.views.VISUAL;
  public availableServices: {
    checked: boolean;
    service: Healthcareservice;
  }[];
  private onDestroy$ = new Subject<void>();
  public availableLangs: ITranslation[] = [];
  public availableCategories: ACTIVITY_CATEGORY_NAME[];
  public separatorKeysCodes: number[] = [ENTER, COMMA];
  public drugServerSideFilteringCtrl: UntypedFormControl = new UntypedFormControl(undefined, [Validators.required, this.noEmptyValue]);
  public lastKeystroke: string; // this variable is used to detect keystroke on child component and trigger reload of the pipe getActivityName
  public currentLanguage: string = this.sessionService.userLang;
  private isValidJson = true;
  public ACTIVITY_CATEGORY_NAME = ACTIVITY_CATEGORY_NAME;
  private valueChangeIsLinear$: Subscription;
  public typeSelectorDisabled = false;
  public servicesPanelExpanded = false; // unfolded at init if no service is selected (at least one is required)

  constructor(
    public careplanEditorService: CareplanEditorService,
    private careplanTemplateFormHelper: CareplanTemplateFormHelper,
    private healthcareService: HealthcareserviceService,
    private languagesService: LanguagesService,
    private cdr: ChangeDetectorRef,
    private sessionService: SessionService,
    private router: Router,
    private careplanService: CareplansService
  ) {}

  ngOnInit(): void {
    // Refresh translation when needed
    this.sessionService.refreshServerTraductions.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.currentLanguage = this.sessionService.userLang;
    });

    if (this.careplanEditorService.currentCareplanTemplate) {
      this.init();
    } else {
      this.careplanEditorService.careplanTemplateReady$
        .pipe(
          skipWhile((value) => value === false),
          takeUntil(this.onDestroy$),
          tap((_ready) => {
            this.init();
          })
        )
        .subscribe();
    }
    this.languagesService
      .list()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((languages) => {
        this.availableLangs = languages;
      });

    this.careplanEditorService.link2CareplanForm
      .get("defaultNotifyOnInactivity")
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe(async () => {
        try {
          await this.careplanService.updateLink2Careplan(this.careplanEditorService.link2CareplanForm.getRawValue()).toPromise();
        } catch (error) {
          FileLogger.warn("CareplanVisualViewComponent", "defaultNotifyOnInactivity valueChanges");
        }
      });
  }

  ngOnDestroy(): void {
    if (this.router.url.startsWith("/careplanEditor/" + this.careplanEditorService.currentCareplanTemplate?.support[0].reference)) {
      // save the careplanTemplate
      this.saveCareplanTemplate();
    }
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public init(): void {
    this.careplanTemplate = this.careplanEditorService.currentCareplanTemplate;
    if (this.careplanTemplate.activity.length > 0) {
      this.typeSelectorDisabled = true;
    } else {
      this.typeSelectorDisabled = false;
    }

    this.servicesPanelExpanded = this.serviceForm.length === 0;
    this.initHealthcareService();
    this.setupAvailablesCategories();
    this.setupValueChange();
    this.isLoading = false;
  }

  /**
   * Get all available services of the user and setup the list for the form
   */
  private initHealthcareService(): void {
    this.healthcareService
      .watchAvailableServices()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((services) => {
        this.availableServices = services.map((s) => {
          const isChecked = this.careplanTemplate.author.findIndex((a) => a.reference === s.identifier[0].value) !== -1;
          if (isChecked && this.serviceForm.length < 0) {
            this.serviceForm.push(
              new UntypedFormGroup({
                reference: new UntypedFormControl(s.identifier[0].value),
                display: new UntypedFormControl(s.identifier[0].label),
              })
            );
          }
          return { checked: isChecked, service: s };
        });
      });
  }
  /**
   * setup valueChange on form field where it is needed
   */
  private setupValueChange(): void {
    if (this.valueChangeIsLinear$) return;
    this.valueChangeIsLinear$ = this.careplanEditorService.careplanTemplateForm
      .get("isLinear")
      .valueChanges.pipe(
        takeUntil(this.onDestroy$),
        tap(() => {
          this.setupAvailablesCategories();
        })
      )
      .subscribe();
  }

  /**
   * Return the description field form
   */
  public get descriptionForm(): UntypedFormGroup {
    return this.careplanEditorService.careplanTemplateForm.get("description") as UntypedFormGroup;
  }

  /**
   * Return the description.translations field form
   */
  public get descriptionTradForm(): UntypedFormArray {
    return this.descriptionForm.get("translations") as UntypedFormArray;
  }

  /**
   * Return the services field form
   */
  public get serviceForm(): UntypedFormArray {
    return this.careplanEditorService.careplanTemplateForm.get("services") as UntypedFormArray;
  }
  /**
   * Return the activity field form
   */
  public get activityForm(): UntypedFormArray {
    return this.careplanEditorService.careplanTemplateForm.get("activity") as UntypedFormArray;
  }

  /**
   * Return the addresses field form
   */
  public get addressesForm(): UntypedFormArray {
    return this.careplanEditorService.careplanTemplateForm.get("addresses") as UntypedFormArray;
  }

  /**
   * Return the detail  field form of an activity
   * @param i - index of the activity
   */
  private getActivityDetailForm(i: number): UntypedFormGroup {
    return this.activityForm.at(i).get("detail") as UntypedFormGroup;
  }

  /**
   * Return the description.translations field form of an activity
   * @param i - index of the activity
   */
  private getActivityDescriptionTradForm(i: number): UntypedFormArray {
    return this.getActivityDetailForm(i).get("description").get("translations") as UntypedFormArray;
  }

  /**
   * Return the reference.translations  field form of an activity
   * @param i - index of the activity
   */
  private getActivityReferenceTradForm(i: number): UntypedFormArray {
    return this.activityForm.at(i).get("reference").get("display").get("translations") as UntypedFormArray;
  }

  /**
   * Return the display field form of an address
   * @param i - index of the address
   */
  private getAddressDisplayTradForm(i: number): UntypedFormArray {
    return this.addressesForm.at(i).get("display").get("translations") as UntypedFormArray;
  }

  /**
   * Return the drug field form of an activity
   * @param i - index of the activity
   */
  private getActivityDrugForm(i: number): UntypedFormGroup {
    return this.getActivityDetailForm(i).get("drug") as UntypedFormGroup;
  }

  /**
   * Return the scheduledTiming field form of an activity
   * @param i - index of the activity
   */
  private getActivityScheduledTimingForm(i: number): UntypedFormGroup {
    return this.getActivityDetailForm(i).get("scheduledTiming") as UntypedFormGroup;
  }

  /**
   * Return the drug.productList field form of an activity
   * @param i - index of the activity
   */
  private getActivityDrugProductList(i: number): UntypedFormArray {
    return this.getActivityDrugForm(i).get("productList") as UntypedFormArray;
  }
  /**
   * update the form tu set/unset the service to the careplan form
   * @param service - the service that the user clicked on
   */
  public healthcareServiceChange(service: { checked: boolean; service: Healthcareservice }): void {
    service.checked = !service.checked;
    // Display the error as soon as the user unselects the last service
    this.serviceForm.markAsTouched();
    const s = service.service;
    if (service.checked) {
      // add service to form
      this.serviceForm.push(
        new UntypedFormGroup({
          reference: new UntypedFormControl(s.identifier[0].value),
          display: new UntypedFormControl(s.identifier[0].label),
        })
      );
    } else {
      // delete service to form
      const index = this.serviceForm.value.findIndex((sf) => sf.reference == s.identifier[0].value);
      if (index !== -1) {
        this.serviceForm.removeAt(index);
      } else {
        FileLogger.warn("healthcareServiceChange", "Service not found " + s.identifier[0].value);
      }
    }
  }

  /**
   * Add an (empty) activity to the careplan form
   */
  public onAddActivity(): void {
    this.activityForm.push(
      new UntypedFormGroup({
        actionResulting: new UntypedFormArray([]),
        progress: new UntypedFormArray([]),
        reference: new UntypedFormGroup({
          reference: new UntypedFormControl(""),
          display: new UntypedFormGroup({
            isTranslated: new UntypedFormControl(false),
            defaultLang: new UntypedFormControl("fr"),
            translations: new UntypedFormArray([]),
          }),
        }),
        detail: new UntypedFormGroup({
          status: new UntypedFormControl(Careplan.ACTIVITY_STATUS_INACTIVE),
          description: new UntypedFormGroup({
            isTranslated: new UntypedFormControl(false),
            defaultLang: new UntypedFormControl("fr"),
            translations: new UntypedFormArray([]),
          }),
          category: new UntypedFormControl(ACTIVITY_CATEGORY_NAME.DRUG, Validators.required),
          code: new UntypedFormGroup({
            code: new UntypedFormControl("", Validators.required),
            system: new UntypedFormControl("http://snomed.info.sct"),
          }),
          location: new UntypedFormControl({}),
          scheduledTiming: new UntypedFormGroup({
            repeat: new UntypedFormGroup({
              frequency: new UntypedFormControl(""),
              period: new UntypedFormControl(""),
              periodUnits: new UntypedFormControl(""),
            }),
          }),
          drug: new UntypedFormGroup({
            isDrugsLinked: new UntypedFormControl(false),
            isMandatory: new UntypedFormControl(false),
            productList: new UntypedFormArray([]),
          }),
          procedure: new UntypedFormGroup({
            isTeleconsultation: new UntypedFormControl(false),
            withPerformer: new UntypedFormControl(false),
            withAddressAndPhone: new UntypedFormControl(false),
            isCycle: new UntypedFormControl(false),
          }),
          administratorsList: new UntypedFormArray([]),
          administratorReference: new UntypedFormControl(null),
        }),
      })
    );
    this.typeSelectorDisabled = true;
    this.addActivityReferenceTrad(this.activityForm.length - 1);
    this.addActivityDescriptionTrad(this.activityForm.length - 1);
  }

  /**
   * Add an address (=type) to the careplan form
   */
  public onAddAddress(): void {
    this.addressesForm.push(
      new UntypedFormGroup({
        reference: new UntypedFormControl(""),
        display: new UntypedFormGroup({
          isTranslated: new UntypedFormControl(false),
          defaultLang: new UntypedFormControl("fr"),
          translations: new UntypedFormArray([]),
        }),
        status: new UntypedFormControl("inactive"), // inactive, active
      })
    );
    this.addAddressDisplayTrad(this.addressesForm.length - 1);
  }

  /**
   * Delete an activity
   * @param index - the index of the activity to delete
   */
  public onDeleteActivity(index: number): void {
    this.activityForm.removeAt(index);
    if (this.activityForm.value.length > 0) {
      this.typeSelectorDisabled = true;
    } else {
      this.typeSelectorDisabled = false;
    }
  }

  /**
   * Delete an address
   * @param index - the index of the address to delete
   */
  public onDeleteAddress(index: number): void {
    this.addressesForm.removeAt(index);
  }

  /**
   * Add a translation (in a language not already set) for the activity description field
   * @param index - the index of the activity
   */
  private addActivityDescriptionTrad(index: number): void {
    const notAlreadyUsedLang = this.availableLangs.filter((l) => {
      const langs: string[] = this.getActivityDescriptionTradForm(index).value.map((d) => d.lang);
      return !langs.includes(l.term);
    });
    this.getActivityDescriptionTradForm(index).push(
      new UntypedFormGroup({
        translateKey: new UntypedFormControl(""),
        lang: new UntypedFormControl(notAlreadyUsedLang[0]?.term),
      })
    );
  }

  /**
   * Add a translation (in a language not already set) for the activity reference field
   * @param index - the index of the activity
   */
  private addActivityReferenceTrad(index: number): void {
    const notAlreadyUsedLang = this.availableLangs.filter((l) => {
      const langs: string[] = this.getActivityReferenceTradForm(index).value.map((d) => d.lang);
      return !langs.includes(l.term);
    });
    this.getActivityReferenceTradForm(index).push(
      new UntypedFormGroup({
        translateKey: new UntypedFormControl("", Validators.required),
        lang: new UntypedFormControl(notAlreadyUsedLang[0]?.term),
      })
    );
  }

  /**
   * Add a translation (in a language not already set) for the address display field
   * @param index - the index of the activity
   */
  private addAddressDisplayTrad(index: number): void {
    const notAlreadyUsedLang = this.availableLangs.filter((l) => {
      const langs: string[] = this.getAddressDisplayTradForm(index).value.map((d) => d.lang);
      return !langs.includes(l.term);
    });
    this.getAddressDisplayTradForm(index).push(
      new UntypedFormGroup({
        translateKey: new UntypedFormControl("", Validators.required),
        lang: new UntypedFormControl(notAlreadyUsedLang[0]?.term),
      })
    );
  }

  /**
   * Return the administratorsList field form of an activity
   * @param activityIndex - index of the activity
   */
  public getActivityAdministratorsForm(activityIndex: number): UntypedFormArray {
    return this.getActivityDetailForm(activityIndex).get("administratorsList") as UntypedFormArray;
  }

  /**
   * Return the display.translations field form of an administrator
   * @param activityIndex - index of the activity
   * @param administratorIndex - index of the administrator
   */
  private getAdministratorDisplayTradForm(activityIndex: number, administratorIndex: number): UntypedFormArray {
    return this.getActivityAdministratorsForm(activityIndex).at(administratorIndex).get("display").get("translations") as UntypedFormArray;
  }

  /**
   * Add an (empty) administrator to a drug activity
   * @param activityIndex - the index of the activity
   */
  public onAddAdministrator(activityIndex: number): void {
    const administrators = this.getActivityAdministratorsForm(activityIndex);
    administrators.push(
      new UntypedFormGroup({
        reference: new UntypedFormControl(uuid()),
        displayTerm: new UntypedFormControl(""),
        display: new UntypedFormGroup({
          isTranslated: new UntypedFormControl(false),
          defaultLang: new UntypedFormControl("fr"),
          translations: new UntypedFormArray([]),
        }),
      })
    );
    this.addAdministratorDisplayTrad(activityIndex, administrators.length - 1);
  }

  /**
   * Delete an administrator from a drug activity. Clears the default reference if it pointed to this administrator.
   * @param activityIndex - the index of the activity
   * @param administratorIndex - the index of the administrator
   */
  public onDeleteAdministrator(activityIndex: number, administratorIndex: number): void {
    const administrators = this.getActivityAdministratorsForm(activityIndex);
    const removedReference = administrators.at(administratorIndex).get("reference").value;
    administrators.removeAt(administratorIndex);
    const administratorReferenceControl = this.getActivityDetailForm(activityIndex).get("administratorReference");
    if (administratorReferenceControl?.value === removedReference) {
      administratorReferenceControl.setValue(null);
    }
  }

  /**
   * Add a translation (in a language not already set) for an administrator display field
   * @param activityIndex - the index of the activity
   * @param administratorIndex - the index of the administrator
   */
  private addAdministratorDisplayTrad(activityIndex: number, administratorIndex: number): void {
    const tradForm = this.getAdministratorDisplayTradForm(activityIndex, administratorIndex);
    const notAlreadyUsedLang = this.availableLangs.filter((l) => {
      const langs: string[] = tradForm.value.map((d) => d.lang);
      return !langs.includes(l.term);
    });
    tradForm.push(
      new UntypedFormGroup({
        translateKey: new UntypedFormControl("", Validators.required),
        lang: new UntypedFormControl(notAlreadyUsedLang[0]?.term),
      })
    );
  }

  /**
   * Remove a drug from the productList of the activity
   * @param item - the refrence of the drug
   * @param i - the index of the activity
   */
  public removeDrug(item: IReference, i: number): void {
    const index = this.getActivityDrugProductList(i).value.findIndex((v) => v.reference === item.reference);
    if (index !== -1) {
      this.getActivityDrugProductList(i).removeAt(index);
    }
  }

  /**
   * Add a drug to the productList of the activity
   * @param value - the name of the drug
   * @param i - the index of the activity
   */
  public addDrugToChip(value: string, i: number): void {
    this.getActivityDrugProductList(i).push(
      new UntypedFormGroup({
        reference: new UntypedFormControl(value),
        display: new UntypedFormControl(value),
      })
    );
    this.productListDrugInput.nativeElement.value = "";
  }
  /**
   * Validator for the drugServerSideFilteringCtrl
   * @param control
   */
  private noEmptyValue(control: UntypedFormControl) {
    let isValid: boolean;
    // control.value can be a string or IDrugInfo if an item has been selected from the list
    if (Tools.isString(control.value)) {
      isValid = control.value?.trim();
    } else {
      isValid = control.value?.officialName?.trim();
    }
    return isValid ? null : { whitespace: true };
  }

  /**
   * Setup the ref of the matAutoComplete input from the drugServerSideSearchComponent to have access to it in the matchiplist input
   * @param event - the ref of the matAutoComplete input from the drugServerSideSearchComponent
   */
  public ref(event: ElementRef): void {
    if (event) {
      this.auto = event;
      this.cdr.detectChanges();
    }
  }

  private setupAvailablesCategories(): void {
    this.availableCategories = this.careplanEditorService.careplanTemplateForm.get("isLinear").value
      ? [
          ACTIVITY_CATEGORY_NAME.DRUG,
          ACTIVITY_CATEGORY_NAME.APPOINTMENT,
          ACTIVITY_CATEGORY_NAME.TELECONSULTATION,
          ACTIVITY_CATEGORY_NAME.OTHER,
        ]
      : [ACTIVITY_CATEGORY_NAME.DRUG, ACTIVITY_CATEGORY_NAME.PROCEDURE, ACTIVITY_CATEGORY_NAME.SUPPLY, ACTIVITY_CATEGORY_NAME.OTHER];
  }

  public frequencyChange(index: number, frequency: ITiming): void {
    this.getActivityScheduledTimingForm(index).get("repeat.frequency").setValue(frequency.frequency);
    this.getActivityScheduledTimingForm(index).get("repeat.period").setValue(frequency.period);
    this.getActivityScheduledTimingForm(index).get("repeat.periodUnits").setValue(frequency.periodUnits);
  }
  /**
   * Save the careplan template (If the json is valid)
   */
  private saveCareplanTemplate(): void {
    this.careplanEditorService.save();
  }

  /** Set the status value based on the checkbox state  */
  public statusChange(control: UntypedFormControl, checked: boolean): void {
    control.setValue(checked ? Careplan.ACTIVITY_STATUS_ACTIVE : Careplan.ACTIVITY_STATUS_INACTIVE);
  }

  public updateActivityCode(value: string, activityFG: UntypedFormGroup): void {
    const codeControl = activityFG.get("detail.code.code");
    if (value === ACTIVITY_CATEGORY_NAME.TELECONSULTATION) {
      codeControl.setValue(FHIRHelper.SNOMED_TELECONSULTATION);
      codeControl.disable();
    } else {
      if (codeControl.value === FHIRHelper.SNOMED_TELECONSULTATION) {
        codeControl.setValue("");
      }
      codeControl.enable();
    }
  }

  public onIsCycleChange($event: MatCheckboxChange, activity: UntypedFormGroup): void {
    const controlpaths = [
      "detail.scheduledTiming.repeat.frequency",
      "detail.scheduledTiming.repeat.period",
      "detail.scheduledTiming.repeat.periodUnits",
    ];
    this.careplanTemplateFormHelper.toggleRequiredValidators($event.checked, activity, controlpaths);
  }

  public comparePeriodUnitsFn(o1: never, o2: never): boolean {
    return Tools.standardizePeriodUnit(o1) === Tools.standardizePeriodUnit(o2);
  }

  public togglePeriod($event: MatSlideToggleChange): void {
    const careplanForm = this.careplanEditorService.careplanTemplateForm;

    if ($event.checked) {
      careplanForm.setControl("period", this.careplanTemplateFormHelper.createPeriodFormGroup(true));
    } else {
      careplanForm.removeControl("period");
    }
  }
}
